import React, { useMemo, useEffect, useState } from "react";
import { Card, Col, Dropdown } from "react-bootstrap";
import TableContainer from "../../Common/TableContainer";
import { Link } from "react-router-dom";
import CustomDropdownToggle from "Common/CustomDropdownToggle";
import { fetchOrders } from "api/apis";
import { Order } from "interface";

const RecentOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        // Fetch more orders than we need in case some get filtered out
        const data = await fetchOrders(1, 15); // Fetch first 15 orders
        // Sort by date (newest first) and take the first 8
        const sortedOrders = data.orders.sort(
          (a: Order, b: Order) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );
        setOrders(sortedOrders.slice(0, 8)); // Take only the first 8 most recent
      } catch (error) {
        console.error("Error loading orders:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const transformedOrders = useMemo(() => {
    return orders.map((order, index) => {
      const product = order.products?.[0]?.product;
      const productImage = product?.imageUrls?.[0] || "";
      const customerName = order.user?.firstName || "Unknown Customer";
      const orderDate = new Date(order.created);
      const formattedOrderDate = orderDate.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

      return {
        id: `${index + 1}`,
        purchaseID: `#${order._id.substring(0, 8).toUpperCase()}`,
        customerName: customerName,
        productName: product?.name || "Unknown Product",
        productImage: productImage,
        amount: `₹${order.total || 0}`,
        orderDate: formattedOrderDate,
        deliveryDate: "N/A",
        vendor: "N/A",
        rating: "4.5",
        status: order.status || "Not processed",
      };
    });
  }, [orders]);

  const columns = useMemo(
    () => [
      {
        Header: "Order ID",
        disableFilters: true,
        filterable: true,
        accessor: (cellProps: any) => {
          return (
            <Link to="#" className="fw-medium link-primary">
              {cellProps.purchaseID}
            </Link>
          );
        },
      },
      {
        Header: "Customer Name",
        accessor: "customerName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Product Name",
        disableFilters: true,
        filterable: true,
        accessor: (cellProps: any) => {
          return (
            <div className="d-flex align-items-center">
              {cellProps.productImage && (
                <div className="flex-shrink-0 me-2">
                  <img
                    src={cellProps.productImage}
                    alt={cellProps.productName}
                    className="avatar-xxs rounded-circle"
                  />
                </div>
              )}
              <div className="flex-grow-1">{cellProps.productName}</div>
            </div>
          );
        },
      },
      {
        Header: "Amount",
        accessor: "amount",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Order Date",
        accessor: "orderDate",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Status",
        disableFilters: true,
        filterable: true,
        accessor: (cellProps: any) => {
          switch (cellProps.status) {
            case "Delivered":
              return (
                <span className="badge bg-success-subtle text-success">
                  {cellProps.status}
                </span>
              );
            case "Cancelled":
              return (
                <span className="badge bg-danger-subtle text-danger">
                  {cellProps.status}
                </span>
              );
            case "Processing":
              return (
                <span className="badge bg-warning-subtle text-warning">
                  {cellProps.status}
                </span>
              );
            case "Shipped":
              return (
                <span className="badge bg-info-subtle text-info">
                  {cellProps.status}
                </span>
              );
            case "Not processed":
              return (
                <span className="badge bg-secondary-subtle text-secondary">
                  {cellProps.status}
                </span>
              );
            default:
              return (
                <span className="badge bg-success-subtle text-success">
                  {cellProps.status}
                </span>
              );
          }
        },
      },
    ],
    []
  );

  if (loading) {
    return (
      <Col xxl={12}>
        <Card>
          <Card.Body className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  }

  return (
    <React.Fragment>
      <Col xxl={12}>
        <Card>
          <Card.Header className="align-items-center d-flex">
            <h4 className="card-title mb-0 flex-grow-1">Recent Orders</h4>
          </Card.Header>

          <TableContainer
            columns={columns || []}
            data={transformedOrders || []}
            isGlobalFilter={false}
            iscustomPageSize={false}
            isBordered={false}
            customPageSize={8} // Show exactly 8 orders
            className="custom-header-css table-card"
            tableClass="table-centered align-middle table-nowrap mb-0"
            theadClass="text-muted table-light"
            SearchPlaceholder="Search recent orders..."
          />
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default RecentOrders;
